import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { createContext, useContext, type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

type Variant = 'underline' | 'pills' | 'boxed';
type Size = 'sm' | 'md' | 'lg';

// ── Variants ──────────────────────────────────────────────────────────────────

const tabSwitchVariants = tv({
  slots: {
    root: '',
    list: 'flex',
    tab: [
      'inline-flex items-center gap-1.5 font-medium cursor-pointer select-none whitespace-nowrap',
      'transition-colors [outline:none] focus-visible:ring-2',
      'disabled:pointer-events-none disabled:opacity-50'
    ],
    panels: 'mt-3',
    panel: 'focus:outline-none'
  },
  variants: {
    variant: {
      underline: {
        list: 'border-b border-uniform-gray-200 dark:border-uniform-gray-700 gap-6',
        tab: [
          'px-1 border-b-2 border-transparent -mb-px',
          'text-uniform-gray-600 hover:text-uniform-gray-700 hover:border-uniform-gray-300',
          'dark:text-uniform-gray-400 dark:hover:text-uniform-gray-200 dark:hover:border-uniform-gray-600',
          'data-[selected]:border-uniform-gray-900 data-[selected]:text-uniform-gray-900',
          'dark:data-[selected]:border-uniform-gray-100 dark:data-[selected]:text-uniform-gray-100',
          'focus-visible:ring-uniform-gray-400 focus-visible:ring-offset-1'
        ]
      },
      pills: {
        list: 'bg-uniform-gray-200 dark:bg-uniform-gray-700 p-1 rounded-lg gap-0.5 inline-flex',
        tab: [
          'rounded-md px-3',
          'text-uniform-gray-700 hover:text-uniform-gray-800',
          'dark:text-uniform-gray-200 dark:hover:text-uniform-gray-100',
          'data-[selected]:bg-white data-[selected]:shadow-xs data-[selected]:text-uniform-gray-900',
          'dark:data-[selected]:bg-uniform-gray-900 dark:data-[selected]:text-uniform-gray-100',
          'focus-visible:ring-uniform-gray-400 focus-visible:ring-offset-1'
        ]
      },
      boxed: {
        list: [
          'border border-b-0 border-uniform-gray-200 dark:border-uniform-gray-700 rounded-t-lg',
          'divide-x divide-uniform-gray-200 dark:divide-uniform-gray-700'
        ],
        tab: [
          'px-5 bg-uniform-gray-50 hover:bg-uniform-gray-100',
          'dark:bg-uniform-gray-800 dark:hover:bg-uniform-gray-700',
          'text-uniform-gray-600 hover:text-uniform-gray-700',
          'dark:text-uniform-gray-300 dark:hover:text-uniform-gray-200',
          'data-[selected]:bg-white data-[selected]:text-uniform-gray-900',
          'dark:data-[selected]:bg-uniform-gray-900 dark:data-[selected]:text-uniform-gray-100',
          'focus-visible:ring-inset focus-visible:ring-uniform-gray-400',
          'first:rounded-tl-lg last:rounded-tr-lg'
        ]
      }
    },
    size: {
      sm: { tab: 'py-1.5 text-xs' },
      md: { tab: 'py-2 text-sm' },
      lg: { tab: 'py-2.5 text-base' }
    },
    stretch: {
      true: { list: 'w-full', tab: 'flex-1 justify-center' }
    }
  },
  compoundVariants: [
    // pills uses inline-flex by default; override to flex when stretched
    { variant: 'pills', stretch: true, class: { list: 'flex' } }
  ],
  defaultVariants: {
    variant: 'underline',
    size: 'md',
    stretch: false
  }
});

type TabSwitchVariants = VariantProps<typeof tabSwitchVariants>;

// ── Context ───────────────────────────────────────────────────────────────────

type TabSwitchCtx = { variant: Variant; size: Size; stretch: boolean };

const TabSwitchContext = createContext<TabSwitchCtx>({
  variant: 'underline',
  size: 'md',
  stretch: false
});

// ── TabSwitch (root) ──────────────────────────────────────────────────────────

interface TabSwitchProps {
  variant?: Variant;
  size?: Size;
  /** Expand tabs to fill the full container width, evenly distributed */
  stretch?: boolean;
  /** Controlled: active tab index */
  selectedIndex?: number;
  onChange?: (index: number) => void;
  /** Uncontrolled: initial active tab index */
  defaultIndex?: number;
  children?: ReactNode;
  className?: string;
}

function TabSwitch({
  variant = 'underline',
  size = 'md',
  stretch = false,
  selectedIndex,
  onChange,
  defaultIndex,
  children,
  className
}: TabSwitchProps) {
  const { root } = tabSwitchVariants({ variant, size, stretch });
  return (
    <TabSwitchContext.Provider value={{ variant, size, stretch }}>
      <TabGroup
        selectedIndex={selectedIndex}
        onChange={onChange}
        defaultIndex={defaultIndex}
        className={cn(root(), className)}>
        {children}
      </TabGroup>
    </TabSwitchContext.Provider>
  );
}

// ── TabSwitchList ─────────────────────────────────────────────────────────────

interface TabSwitchListProps {
  children?: ReactNode;
  className?: string;
}

function TabSwitchList({ children, className }: TabSwitchListProps) {
  const { variant, size, stretch } = useContext(TabSwitchContext);
  const { list } = tabSwitchVariants({ variant, size, stretch });
  return <TabList className={cn(list(), className)}>{children}</TabList>;
}

// ── TabSwitchTab ──────────────────────────────────────────────────────────────

interface TabSwitchTabProps {
  children?: ReactNode;
  icon?: IconDefinition;
  disabled?: boolean;
  className?: string;
}

function TabSwitchTab({ children, icon, disabled, className }: TabSwitchTabProps) {
  const { variant, size, stretch } = useContext(TabSwitchContext);
  const { tab } = tabSwitchVariants({ variant, size, stretch });
  return (
    <Tab disabled={disabled} className={cn(tab(), className)}>
      {icon && <FontAwesomeIcon icon={icon} className="shrink-0" />}
      {children}
    </Tab>
  );
}

// ── TabSwitchPanels ───────────────────────────────────────────────────────────

interface TabSwitchPanelsProps {
  children?: ReactNode;
  className?: string;
}

function TabSwitchPanels({ children, className }: TabSwitchPanelsProps) {
  const { variant, size } = useContext(TabSwitchContext);
  const { panels } = tabSwitchVariants({ variant, size });
  return <TabPanels className={cn(panels(), className)}>{children}</TabPanels>;
}

// ── TabSwitchPanel ────────────────────────────────────────────────────────────

interface TabSwitchPanelProps {
  children?: ReactNode;
  className?: string;
}

function TabSwitchPanel({ children, className }: TabSwitchPanelProps) {
  const { variant, size } = useContext(TabSwitchContext);
  const { panel } = tabSwitchVariants({ variant, size });
  return <TabPanel className={cn(panel(), className)}>{children}</TabPanel>;
}

export {
  TabSwitch,
  TabSwitchList,
  TabSwitchTab,
  TabSwitchPanels,
  TabSwitchPanel,
  tabSwitchVariants
};
export type {
  TabSwitchProps,
  TabSwitchListProps,
  TabSwitchTabProps,
  TabSwitchPanelsProps,
  TabSwitchPanelProps,
  TabSwitchVariants
};
